import { useMemo } from "react";

import i18next from "i18next";
import moment from "moment";
import { IoAirplane, IoCheckmarkCircle, IoCloseCircle, IoTimeOutline } from "react-icons/io5";
import { useNavigate } from "react-router-dom";

import palette from "../../../theme/palette";
import { ContainerComponent, DotsLoadingComponent, TableComponent } from "../../ui";
import OrderRefundLink from "../OrderRefundLink";
import OrderSupportLink, { OrderActions } from "../OrderSupportLink";

import { LoadingBlock } from "../../../pages/ResultPage/style";

import { formatDateTime } from "../../../config";
import {
  APPLICATION_REFUND_CODE,
  BOOKING_CANCEL,
  BOOKING_CANCEL_ERROR_CODE,
  BOOKING_CANCEL_FROM_ADMIN_CODE,
  BOOKING_CANCEL_FROM_CLIENT_CODE,
  BOOKING_DONE_CODE,
  CERTIFICATE_ISSUED_CODE,
  EXCHANGE_CODE,
  IN_PROGRESS_BOOK_CODE,
  PARTIAL_REFUND_CODE,
  PAY_SUCCESS_CODE,
  REFUND_CODE,
  TICKETING_DONE_CODE,
  VOID_CODE,
} from "../../../constants/order";
import { getBookStatus, getColorStatus, normalizePrice } from "../../../utils";
import {
  HeaderWithSubtitle,
  MobileOrderCard,
  MobileOrderCardHeader,
  MobileOrderCardLabel,
  MobileOrderCardRow,
  MobileOrderCardValue,
  MobileOrdersContainer,
  NotFoundComponent,
  StatusBadge,
  StatusCard,
  StatusCardContent,
  StatusCardIcon,
  StatusCardLabel,
  StatusCardNumber,
  StatusSummaryCards,
  Subtitle,
  TitleComponent,
  Wrapper,
} from "./style";

const OrdersComponent = ({ isLoading, orders, included }) => {
  const { language, t } = i18next;
  const navigate = useNavigate();

  // Calculate status counts
  const statusCounts = useMemo(() => {
    if (!orders || !Array.isArray(orders)) {
      return { successful: 0, cancelled: 0, timeout: 0 };
    }

    return orders.reduce(
      (acc, order) => {
        const status = order.booking_status;

        if (status === TICKETING_DONE_CODE || status === CERTIFICATE_ISSUED_CODE) {
          acc.successful++;
        } else if (status === BOOKING_CANCEL_FROM_CLIENT_CODE || status === BOOKING_CANCEL_FROM_ADMIN_CODE) {
          acc.cancelled++;
        } else if (status === BOOKING_CANCEL) {
          acc.timeout++;
        }

        return acc;
      },
      { successful: 0, cancelled: 0, timeout: 0 }
    );
  }, [orders]);

  const Routes = ({ routes }) =>
    routes.map(route => {
      const departureAirportCode = route?.departure_airport_code;
      const arrivalAirportCode = route?.arrival_airport_code;
      const departureAirportCityCode = included.airport[departureAirportCode].city_code;
      const arrivalAirportCityCode = included.airport[arrivalAirportCode].city_code;
      const departureCity = included.city[departureAirportCityCode][language];
      const arrivalCity = included.city[arrivalAirportCityCode][language];

      return (
        <div key={route.id} style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <IoAirplane style={{ fontSize: 16, color: palette.textSecondary }} />
          {`${departureCity} → ${arrivalCity}`}
        </div>
      );
    });

  const DepartureTime = ({ routes }) => {
    const route = routes?.length ? routes[0] : {};
    const time = moment(route?.departure_at, formatDateTime);

    return (
      <>
        <div>{time.format("DD.MM.YYYY")}</div>
        <div>{time.format("HH:mm")}</div>
      </>
    );
  };

  const Timelimit = ({ timelimit }) => {
    const timelimitUtc = moment.utc(timelimit, formatDateTime).tz(moment.tz.guess());

    return (
      <>
        <div>{timelimitUtc.format("DD.MM.YYYY")}</div>
        <div>{timelimitUtc.format("HH:mm:ss")}</div>
      </>
    );
  };

  const StatusBadgeComponent = ({ bookingStatus, orderStatus }) => {
    const fullStatus = getBookStatus(bookingStatus, orderStatus, t);
    const color = getColorStatus(bookingStatus);

    let icon = null;
    let statusText = fullStatus;
    let description = "";

    // Success statuses
    if (bookingStatus === TICKETING_DONE_CODE || bookingStatus === CERTIFICATE_ISSUED_CODE) {
      icon = <IoCheckmarkCircle />;
      statusText = t("success");
      description = t("successful_payment_tickets_issued");
    }
    // Cancelled by customer/admin
    else if (bookingStatus === BOOKING_CANCEL_FROM_CLIENT_CODE || bookingStatus === BOOKING_CANCEL_FROM_ADMIN_CODE) {
      icon = <IoCloseCircle />;
      statusText = t("cancelled");
      description = t("order_cancelled_by_customer");
    }
    // Timeout
    else if (bookingStatus === BOOKING_CANCEL) {
      icon = <IoTimeOutline />;
      statusText = t("timeout");
      description = t("order_cancelled_by_time_limit");
    }
    // Refund statuses
    else if (
      bookingStatus === REFUND_CODE ||
      bookingStatus === PARTIAL_REFUND_CODE ||
      bookingStatus === APPLICATION_REFUND_CODE
    ) {
      statusText = t("status_refund");
      description = fullStatus;
    }
    // Exchange
    else if (bookingStatus === EXCHANGE_CODE) {
      statusText = t("status_exchange");
      description = fullStatus;
    }
    // Void
    else if (bookingStatus === VOID_CODE) {
      statusText = t("status_void");
      description = fullStatus;
    }
    // Cancel error
    else if (bookingStatus === BOOKING_CANCEL_ERROR_CODE) {
      statusText = t("status_error");
      description = fullStatus;
    }
    // Booked
    else if (bookingStatus === BOOKING_DONE_CODE) {
      statusText = t("status_booked");
      description = fullStatus;
    }
    // Processing
    else if (bookingStatus === IN_PROGRESS_BOOK_CODE || bookingStatus === PAY_SUCCESS_CODE) {
      statusText = t("status_processing");
      description = fullStatus;
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
        <StatusBadge color={color}>
          {icon && <span className="icon">{icon}</span>}
          {statusText}
        </StatusBadge>
        {description && (
          <div style={{ fontSize: 12, color: palette.textSecondary, maxWidth: 180, lineHeight: 1.3 }}>{description}</div>
        )}
      </div>
    );
  };

  const renderMobileOrders = () => {
    return (
      <MobileOrdersContainer>
        {orders.map(order => (
          <MobileOrderCard key={order.id} onClick={() => navigate(`/order/${order?.order_id}/${order?.session_id}`)}>
            <MobileOrderCardHeader>
              <div>
                <IoAirplane style={{ fontSize: 20 }} />
                {order.routes.map(route => {
                  const departureAirportCode = route?.departure_airport_code;
                  const arrivalAirportCode = route?.arrival_airport_code;
                  const departureAirportCityCode = included.airport[departureAirportCode].city_code;
                  const arrivalAirportCityCode = included.airport[arrivalAirportCode].city_code;
                  const departureCity = included.city[departureAirportCityCode][language];
                  const arrivalCity = included.city[arrivalAirportCityCode][language];
                  return <span key={route.id}>{`${departureCity} → ${arrivalCity}`}</span>;
                })}
              </div>
              <StatusBadgeComponent bookingStatus={order.booking_status} orderStatus={order.order_status} />
            </MobileOrderCardHeader>

            <MobileOrderCardRow>
              <MobileOrderCardLabel>{t("order_num")}</MobileOrderCardLabel>
              <MobileOrderCardValue>{order.order_id}</MobileOrderCardValue>
            </MobileOrderCardRow>

            <MobileOrderCardRow>
              <MobileOrderCardLabel>PNR:</MobileOrderCardLabel>
              <MobileOrderCardValue>{order.booking_number}</MobileOrderCardValue>
            </MobileOrderCardRow>

            <MobileOrderCardRow>
              <MobileOrderCardLabel>{t("departure_time")}</MobileOrderCardLabel>
              <MobileOrderCardValue>
                <DepartureTime routes={order.routes} />
              </MobileOrderCardValue>
            </MobileOrderCardRow>

            <MobileOrderCardRow>
              <MobileOrderCardLabel>{t("pay_before")}</MobileOrderCardLabel>
              <MobileOrderCardValue>
                <Timelimit timelimit={order.timelimit} />
              </MobileOrderCardValue>
            </MobileOrderCardRow>

            <MobileOrderCardRow>
              <MobileOrderCardLabel>{t("order_cost")}</MobileOrderCardLabel>
              <MobileOrderCardValue className="price">
                {normalizePrice(order?.price)} {order?.currency}
              </MobileOrderCardValue>
            </MobileOrderCardRow>

            <OrderActions>
              <OrderRefundLink
                orderId={order.order_id}
                sessionId={order.session_id}
                bookingStatus={order.booking_status}
                orderStatus={order.order_status}
                fullWidth
              />
              <OrderSupportLink orderId={order.order_id} fullWidth />
            </OrderActions>
          </MobileOrderCard>
        ))}
      </MobileOrdersContainer>
    );
  };

  return (
    <Wrapper>
      <ContainerComponent>
        <HeaderWithSubtitle>
          <TitleComponent>{t("orders")}</TitleComponent>
          <Subtitle>{t("orders_subtitle")}</Subtitle>
        </HeaderWithSubtitle>

        {isLoading && (
          <LoadingBlock>
            <DotsLoadingComponent />
          </LoadingBlock>
        )}

        {!isLoading && !orders?.length && <NotFoundComponent>У вас нет заказов!</NotFoundComponent>}

        {!isLoading && orders?.length ? (
          <StatusSummaryCards>
            <StatusCard variant="success">
              <StatusCardIcon variant="success">
                <IoCheckmarkCircle />
              </StatusCardIcon>
              <StatusCardContent>
                <StatusCardNumber>{statusCounts.successful}</StatusCardNumber>
                <StatusCardLabel>{t("successful")}</StatusCardLabel>
              </StatusCardContent>
            </StatusCard>

            <StatusCard variant="cancelled">
              <StatusCardIcon variant="cancelled">
                <IoCloseCircle />
              </StatusCardIcon>
              <StatusCardContent>
                <StatusCardNumber>{statusCounts.cancelled}</StatusCardNumber>
                <StatusCardLabel>{t("cancelled")}</StatusCardLabel>
              </StatusCardContent>
            </StatusCard>

            <StatusCard variant="timeout">
              <StatusCardIcon variant="timeout">
                <IoTimeOutline />
              </StatusCardIcon>
              <StatusCardContent>
                <StatusCardNumber>{statusCounts.timeout}</StatusCardNumber>
                <StatusCardLabel>{t("timeout")}</StatusCardLabel>
              </StatusCardContent>
            </StatusCard>
          </StatusSummaryCards>
        ) : null}
      </ContainerComponent>

      <ContainerComponent>
        {!isLoading && orders?.length ? (
          <>
            <div className="orders-container desktop-view">
              <TableComponent
                columns={[
                  {
                    key: "order_id",
                    label: t("order_details"),
                    width: "16%",
                    render: column => (
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>{column.order_id}</div>
                        <div style={{ fontSize: 12, color: palette.textSecondary, marginTop: 2 }}>PNR: {column.booking_number}</div>
                      </div>
                    ),
                  },
                  {
                    key: "routes",
                    label: t("flight_info"),
                    width: "16%",
                    render: column => <Routes routes={column.routes} />,
                  },
                  {
                    key: "departure_time",
                    label: t("departure"),
                    width: "12%",
                    render: column => <DepartureTime routes={column.routes} />,
                  },
                  {
                    key: "price",
                    label: t("price"),
                    width: "14%",
                    render: column => (
                      <div style={{ fontWeight: 600, color: palette.textPrimary }}>
                        {normalizePrice(column?.price)} {column?.currency}
                      </div>
                    ),
                  },
                  {
                    key: "timelimit",
                    label: t("pay_before"),
                    width: "14%",
                    render: column => <Timelimit timelimit={column.timelimit} />,
                  },
                  {
                    key: "booking_status",
                    label: t("status"),
                    width: "20%",
                    render: column => (
                      <StatusBadgeComponent bookingStatus={column.booking_status} orderStatus={column.order_status} />
                    ),
                  },
                  {
                    key: "action",
                    label: t("action"),
                    width: "10%",
                    render: column => (
                      <OrderActions>
                        <OrderRefundLink
                          orderId={column.order_id}
                          sessionId={column.session_id}
                          bookingStatus={column.booking_status}
                          orderStatus={column.order_status}
                        />
                        <OrderSupportLink orderId={column.order_id} />
                      </OrderActions>
                    ),
                  },
                ]}
                data={orders}
                isClickableRow
                onClickRow={column => navigate(`/order/${column?.order_id}/${column?.session_id}`)}
              />
            </div>

            <div className="mobile-view">{renderMobileOrders()}</div>
          </>
        ) : null}
      </ContainerComponent>
    </Wrapper>
  );
};

export default OrdersComponent;
